import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography
} from "@mui/material";

interface IdleLogoutProps {
  isShowingWarning: boolean;
  setIsShowingWarning: (isShowingWarning: boolean) => void;
  updateIdleExpiryTime: () => void;
  onLogout: () => void;
}

export const IdleLogout = ({
  isShowingWarning,
  setIsShowingWarning,
  updateIdleExpiryTime,
  onLogout
}: IdleLogoutProps) => {
  const [elapsed, setElapsed] = useState(0);
  const TOTAL_TIME = 30;

  // Progress counter
  useEffect(() => {
    if (!isShowingWarning) return;

    setElapsed(0); // reset
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isShowingWarning]);

  useEffect(() => {
    if (elapsed >= TOTAL_TIME) {
      setIsShowingWarning(false);
      onLogout();
    }
  }, [elapsed, onLogout, setIsShowingWarning]);

  const remaining = TOTAL_TIME - elapsed;

  return (
    <Dialog
      open={isShowingWarning}
      onClose={() => setIsShowingWarning(false)}
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      <DialogTitle id="session-timeout-title"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
      >Session Timeout</DialogTitle>
      <DialogContent>
        <Typography>
          For your security, you will be logged out due to inactivity.
        </Typography>
        <div style={{ marginTop: "16px", marginBottom: "8px" }}>
          <LinearProgress
            variant="determinate"
            value={(elapsed / TOTAL_TIME) * 100}
            sx={{
              height: "20px",
              border: "1px solid #C8D7E1",
              borderRadius: "5px",
              backgroundColor: "#FFF",
              "& .MuiLinearProgress-bar": {
                backgroundImage:
                  "linear-gradient(90deg, #7C90A3 0%, #C8D7E1 100%)",
                backgroundColor: "transparent"
              }
            }}
          />
        </div>
        <Typography align="center">
          You'll be logged out in {remaining} second{remaining !== 1 ? "s" : ""}
        </Typography>
      </DialogContent>
      <DialogActions
        style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Button
          onClick={() => {
            updateIdleExpiryTime();
            setIsShowingWarning(false);
          }}
          variant="contained"
          style={{ backgroundColor: "#004F6E", color: "white" }}
        >
          I'm still here
        </Button>
      </DialogActions>
    </Dialog>
  );
};
